import React, { useState, useEffect } from 'react';
import api from '../api';
import CollapsibleFilters from './CollapsibleFilters';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import {
  FormField,
  FormGrid,
  FormSection,
  FormActions,
  StandardInput,
  StandardSelect,
  StandardTextarea,
  QuantityInput,
  ItemList,
  ValidationSummary,
  EnhancedFormField,
  useFormValidation
} from './FormComponents';

interface StockTransfer {
  id: number;
  transfer_number: string;
  from_warehouse_id: number;
  to_warehouse_id: number;
  transfer_date: string;
  expected_arrival?: string;
  status: string;
  notes?: string;
  from_warehouse?: {
    id: number;
    name: string;
  };
  to_warehouse?: {
    id: number;
    name: string;
  };
  items: TransferItem[];
}

interface TransferItem {
  product_id: number;
  quantity_sent: number;
  quantity_received: number;
  notes?: string;
}

interface Warehouse {
  id: number;
  code: string;
  name: string;
}

interface Product {
  id: number;
  sku: string;
  name: string;
  attributes?: Array<{ attribute_name: string; value: string }>;
}

// Helper: format product label as "SKU - Name - Color - Size"
const getProductLabel = (product: Product) => {
  const color = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'color')?.value || '';
  const size = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'size')?.value || '';
  let label = `${product.sku} - ${product.name}`;
  if (color) label += ` - ${color}`;
  if (size) label += ` - ${size}`;
  return label;
};

const StockTransferManager: React.FC = () => {
  const [transfers, setTransfers] = useState<StockTransfer[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(searchText);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [searchText]);
  
  const [formData, setFormData] = useState({
    from_warehouse_id: 0,
    to_warehouse_id: 0,
    expected_arrival: '',
    notes: '',
    items: [] as TransferItem[]
  });

  // Enhanced form validation
  const {
    values: validationValues,
    errors: validationErrors,
    touched: validationTouched,
    setValue: setValidationValue,
    setFieldTouched,
    validateForm,
    resetForm: resetValidation
  } = useFormValidation(
    {
      from_warehouse_id: 0,
      to_warehouse_id: 0,
      items: []
    },
    {
      from_warehouse_id: {
        required: true,
        custom: (value) => value === 0 ? 'Please select a source warehouse' : null
      },
      to_warehouse_id: {
        required: true,
        custom: (value) => value === 0 ? 'Please select a destination warehouse' : null
      },
      items: {
        required: true,
        custom: (value) => {
          if (!Array.isArray(value) || value.length === 0) {
            return 'At least one item is required';
          }
          
          const errors: string[] = [];
          const productIds = new Set();
          
          value.forEach((item, index) => {
            if (!item.product_id) {
              errors.push(`Item ${index + 1}: Please select a product`);
            } else if (productIds.has(item.product_id)) {
              errors.push(`Item ${index + 1}: Duplicate product not allowed`);
            } else {
              productIds.add(item.product_id);
            }
            
            if (item.quantity_sent <= 0) {
              errors.push(`Item ${index + 1}: Quantity must be greater than 0`);
            }
          });
          
          return errors.length > 0 ? errors.join('; ') : null;
        }
      }
    }
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, debouncedSearch, dateFrom, dateTo]);

  const fetchData = async () => {
    try {
      const skip = (currentPage - 1) * pageSize;
      const params: any = { skip, limit: pageSize };
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [transfersRes, warehousesRes, productsRes] = await Promise.all([
        api.get('/inventory/transfers/', { params }),
        api.get('/inventory/warehouses/'),
        api.get('/products/', { params: { limit: 100 } })
      ]);
      setTransfers(transfersRes.data);
      setTotalCount(transfersRes.data.length);
      setWarehouses(warehousesRes.data);
      setProducts(productsRes.data.products || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Search is now server-side, no client-side filtering needed

  const handleDelete = async (id: number) => {
    const transfer = transfers.find(t => t.id === id);
    setDeleteConfirm({show: true, id, name: transfer?.transfer_number || `Transfer #${id}`});
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/inventory/transfers/${deleteConfirm.id}`);
      setDeleteConfirm({show: false, id: null, name: ''});
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to delete transfer');
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const sortedTransfers = [...transfers].sort((a, b) => {
    if (!sortField) return 0;
    let aVal: any = '', bVal: any = '';
    switch (sortField) {
      case 'transfer_number': aVal = a.transfer_number || ''; bVal = b.transfer_number || ''; break;
      case 'transfer_date': aVal = a.transfer_date || ''; bVal = b.transfer_date || ''; break;
      case 'from': aVal = a.from_warehouse?.name || ''; bVal = b.from_warehouse?.name || ''; break;
      case 'to': aVal = a.to_warehouse?.name || ''; bVal = b.to_warehouse?.name || ''; break;
      case 'items': aVal = a.items?.length || 0; bVal = b.items?.length || 0; break;
      case 'status': aVal = a.status || ''; bVal = b.status || ''; break;
      default: return 0;
    }
    if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSelectAll = (checked: boolean) => {
    setSelectedIds(checked ? transfers.map(t => t.id) : []);
  };

  const handleSelectItem = (id: number, checked: boolean) => {
    setSelectedIds(checked ? [...selectedIds, id] : selectedIds.filter(i => i !== id));
  };

  const handleBulkDeleteClick = () => {
    if (selectedIds.length > 1) setShowBulkDeleteConfirm(true);
    else if (selectedIds.length === 1) { handleDelete(selectedIds[0]); setSelectedIds([]); }
  };

  const confirmBulkDelete = async () => {
    if (confirmText !== 'confirm') return;
    const errors: string[] = [];
    for (const id of selectedIds) {
      try { await api.delete(`/inventory/transfers/${id}`); }
      catch (error: any) { errors.push(`Transfer ${id}: ${error.response?.data?.detail || 'Failed'}`); }
    }
    if (errors.length > 0) alert(`Some deletions failed:\n${errors.join('\n')}`);
    setSelectedIds([]);
    setShowBulkDeleteConfirm(false);
    setConfirmText('');
    fetchData();
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: 0, quantity_sent: 0, quantity_received: 0 }]
    });
  };

  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async () => {
    // Sync validation values with form data
    setValidationValue('from_warehouse_id', formData.from_warehouse_id);
    setValidationValue('to_warehouse_id', formData.to_warehouse_id);
    setValidationValue('items', formData.items);
    
    // Validate form
    if (!validateForm()) {
      // Mark all fields as touched to show errors
      setFieldTouched('from_warehouse_id');
      setFieldTouched('to_warehouse_id');
      setFieldTouched('items');
      return;
    }

    if (formData.from_warehouse_id === formData.to_warehouse_id) {
      alert('Source and destination warehouses must be different');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/inventory/transfers/', formData);
      alert('Stock transfer created successfully');
      setShowForm(false);
      setFormData({ from_warehouse_id: 0, to_warehouse_id: 0, expected_arrival: '', notes: '', items: [] });
      resetValidation();
      fetchData();
    } catch (error) {
      console.error('Error creating transfer:', error);
      alert('Failed to create transfer');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await api.put(`/inventory/transfers/${id}`, { status: newStatus });
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      draft: 'status-draft',
      in_transit: 'status-draft',
      received: 'status-active',
      completed: 'status-active',
      cancelled: 'status-inactive'
    };
    return (
      <span className={`status-badge ${statusColors[status] || 'status-draft'}`}>
        {status.replace('_', ' ').charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div>
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <h2 className="card-title">Stock Transfers</h2>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search transfer #, warehouse, status..."
            style={{ padding: '6px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px', width: '100%', maxWidth: '400px' }}
          />
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
          <span className="btn-icon">➕</span>
          New Transfer
        </button>
      </div>

      <CollapsibleFilters label="Filters">
        <div style={{ padding: '10px 20px', borderBottom: '1px solid #f0f3f5', background: '#fafbfc', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <label style={{ fontSize: '12px', color: '#64748b' }}>From:</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #dde2e8', borderRadius: '5px', fontSize: '12px' }} />
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <label style={{ fontSize: '12px', color: '#64748b' }}>To:</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #dde2e8', borderRadius: '5px', fontSize: '12px' }} />
          </div>
          <button className="btn btn-secondary" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</button>
        </div>
      </CollapsibleFilters>

      {selectedIds.length > 0 && (
        <div style={{ padding: '1rem 2rem', background: '#fef3c7', borderBottom: '1px solid #e2e8f0' }}>
          <span>{selectedIds.length} transfers selected</span>
          <button className="btn btn-danger" style={{ marginLeft: '1rem' }} onClick={handleBulkDeleteClick}>
            Delete Selected
          </button>
        </div>
      )}

      <div className="table-container">
        <table className="table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '50px' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '13%' }} />
          </colgroup>
          <thead>
            <tr>
              <th><input type="checkbox" className="checkbox" checked={selectedIds.length === transfers.length && transfers.length > 0} onChange={(e) => handleSelectAll(e.target.checked)} /></th>
              <th onClick={() => handleSort('transfer_number')} style={{ cursor: 'pointer' }}>Transfer # {sortField === 'transfer_number' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('transfer_date')} style={{ cursor: 'pointer' }}>Date {sortField === 'transfer_date' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('from')} style={{ cursor: 'pointer' }}>From {sortField === 'from' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('to')} style={{ cursor: 'pointer' }}>To {sortField === 'to' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('items')} style={{ cursor: 'pointer' }}>Items {sortField === 'items' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {transfers.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                  No transfers found. Create your first stock transfer.
                </td>
              </tr>
            ) : (
              sortedTransfers.map(transfer => (
                <tr key={transfer.id}>
                  <td><input type="checkbox" className="checkbox" checked={selectedIds.includes(transfer.id)} onChange={(e) => handleSelectItem(transfer.id, e.target.checked)} /></td>
                  <td style={{ wordBreak: 'break-word' }}>{transfer.transfer_number}</td>
                  <td>{new Date(transfer.transfer_date).toLocaleDateString()}</td>
                  <td style={{ wordBreak: 'break-word' }}>{transfer.from_warehouse?.name || '-'}</td>
                  <td style={{ wordBreak: 'break-word' }}>{transfer.to_warehouse?.name || '-'}</td>
                  <td>{transfer.items?.length || 0} items</td>
                  <td>
                    <select
                      value={transfer.status}
                      onChange={(e) => handleStatusChange(transfer.id, e.target.value)}
                      className="form-select"
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      <option value="draft">Draft</option>
                      <option value="in_transit">In Transit</option>
                      <option value="received">Received</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td>
                    <button className="btn btn-danger" onClick={() => handleDelete(transfer.id)} style={{ fontSize: '12px', padding: '4px 8px' }}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {transfers.length > 0 && (
        <div style={{ 
          padding: '1rem 2rem', 
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'white'
        }}>
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} transfers
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label style={{ fontSize: '14px', color: '#64748b', marginRight: '0.5rem' }}>
              Per page:
            </label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                border: '1px solid #cbd5e1',
                fontSize: '14px'
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>

            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary"
              style={{ 
                padding: '0.25rem 0.75rem',
                fontSize: '14px',
                opacity: currentPage === 1 ? 0.5 : 1,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Previous
            </button>

            <span style={{ fontSize: '14px', color: '#64748b', padding: '0 0.5rem' }}>
              Page {currentPage} of {Math.ceil(totalCount / pageSize)}
            </span>

            <button
              onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / pageSize), prev + 1))}
              disabled={currentPage >= Math.ceil(totalCount / pageSize)}
              className="btn btn-secondary"
              style={{ 
                padding: '0.25rem 0.75rem',
                fontSize: '14px',
                opacity: currentPage >= Math.ceil(totalCount / pageSize) ? 0.5 : 1,
                cursor: currentPage >= Math.ceil(totalCount / pageSize) ? 'not-allowed' : 'pointer'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showBulkDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Bulk Delete</h3>
            <p>You are about to delete {selectedIds.length} stock transfers. This action cannot be undone.</p>
            <p>Type "confirm" to proceed:</p>
            <input type="text" className="form-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type confirm" />
            <div style={{ marginTop: '1rem', display: 'flex', gap: '8px' }}>
              <button className="btn btn-danger" onClick={confirmBulkDelete} disabled={confirmText !== 'confirm'}>Delete Transfers</button>
              <button className="btn btn-cancel" onClick={() => { setShowBulkDeleteConfirm(false); setConfirmText(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className={`modal ${isSubmitting ? 'form-loading' : ''}`} style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto', padding: '32px' }}>
            <FormSection 
              title="Create Stock Transfer"
              subtitle="Transfer inventory between warehouses"
            >
              <ValidationSummary 
                errors={validationErrors}
                title="Please correct the following errors before submitting:"
              />

              <FormGrid columns={2}>
                <EnhancedFormField 
                  label="From Warehouse" 
                  required 
                  tooltip="Select the source warehouse"
                  error={validationErrors.from_warehouse_id}
                  touched={validationTouched.from_warehouse_id}
                  onBlur={() => setFieldTouched('from_warehouse_id')}
                  helpText="Choose the warehouse to transfer items from"
                >
                  <StandardSelect
                    value={formData.from_warehouse_id}
                    onChange={(e) => {
                      const warehouseId = parseInt(e.target.value);
                      setFormData({ ...formData, from_warehouse_id: warehouseId });
                      setValidationValue('from_warehouse_id', warehouseId);
                    }}
                    error={!!validationErrors.from_warehouse_id}
                  >
                    <option value={0}>Select Warehouse</option>
                    {warehouses.map(wh => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
                  </StandardSelect>
                </EnhancedFormField>

                <EnhancedFormField 
                  label="To Warehouse" 
                  required 
                  tooltip="Select the destination warehouse"
                  error={validationErrors.to_warehouse_id}
                  touched={validationTouched.to_warehouse_id}
                  onBlur={() => setFieldTouched('to_warehouse_id')}
                  helpText="Choose the warehouse to transfer items to"
                >
                  <StandardSelect
                    value={formData.to_warehouse_id}
                    onChange={(e) => {
                      const warehouseId = parseInt(e.target.value);
                      setFormData({ ...formData, to_warehouse_id: warehouseId });
                      setValidationValue('to_warehouse_id', warehouseId);
                    }}
                    error={!!validationErrors.to_warehouse_id}
                  >
                    <option value={0}>Select Warehouse</option>
                    {warehouses.map(wh => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
                  </StandardSelect>
                </EnhancedFormField>
              </FormGrid>

              <EnhancedFormField 
                label="Expected Arrival Date"
                tooltip="When is this transfer expected to arrive? (optional)"
                helpText="Set an expected arrival date to track transfer progress"
              >
                <StandardInput
                  type="date"
                  value={formData.expected_arrival}
                  onChange={(e) => setFormData({ ...formData, expected_arrival: e.target.value })}
                />
              </EnhancedFormField>

              <EnhancedFormField 
                label="Notes"
                tooltip="Additional details about this transfer (optional)"
                helpText="Provide any additional context or special instructions"
              >
                <StandardTextarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder="Enter any additional notes about this transfer..."
                />
              </EnhancedFormField>

              <div style={{ marginTop: '1.5rem' }}>
                <EnhancedFormField
                  label="Transfer Items"
                  required
                  error={validationErrors.items}
                  touched={validationTouched.items}
                  onBlur={() => setFieldTouched('items')}
                  helpText="Add products and specify quantities to transfer"
                >
                  <ItemList
                    items={formData.items}
                    onAddItem={addItem}
                    onRemoveItem={removeItem}
                    onUpdateItem={(index, field, value) => {
                      updateItem(index, field, value);
                      // Update validation when items change
                      const newItems = [...formData.items];
                      newItems[index] = { ...newItems[index], [field]: value };
                      setValidationValue('items', newItems);
                    }}
                    addButtonText="Add Product"
                    emptyMessage="No products added yet. Click 'Add Product' to start."
                    renderItem={(item, index, onUpdate, onRemove) => (
                      <div className="item-grid item-grid-3" style={{ gap: '12px', alignItems: 'flex-end' }}>
                        <FormField label="Product" required>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              placeholder="Type SKU or product name..."
                              defaultValue={item.product_id ? (products.find(p => p.id === item.product_id) ? getProductLabel(products.find(p => p.id === item.product_id)!) : '') : ''}
                              onChange={(e) => {
                                const val = e.target.value.toLowerCase();
                                const match = products.find(p => p.name.toLowerCase() === val || p.sku.toLowerCase() === val || getProductLabel(p).toLowerCase() === val);
                                if (match) onUpdate('product_id', match.id);
                                else if (!e.target.value) onUpdate('product_id', 0);
                              }}
                              list={`products-list-${index}`}
                              style={{ width: '100%', padding: '8px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px' }}
                            />
                            <datalist id={`products-list-${index}`}>
                              {products.map(p => (
                                <option key={p.id} value={getProductLabel(p)}>{getProductLabel(p)}</option>
                              ))}
                            </datalist>
                          </div>
                        </FormField>

                        <QuantityInput
                          label="Quantity"
                          value={item.quantity_sent}
                          onChange={(value) => onUpdate('quantity_sent', value)}
                          tooltip="Quantity to transfer"
                          placeholder="Enter quantity"
                        />

                        <button 
                          type="button"
                          className="item-remove-btn"
                          onClick={onRemove}
                          title="Remove this item"
                          style={{ alignSelf: 'center', marginTop: '20px' }}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  />
                </EnhancedFormField>
              </div>

              <FormActions>
                <button 
                  type="button"
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowForm(false);
                    setFormData({ from_warehouse_id: 0, to_warehouse_id: 0, expected_arrival: '', notes: '', items: [] });
                    resetValidation();
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  className="btn btn-primary" 
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Transfer'}
                </button>
              </FormActions>
            </FormSection>
          </div>
        </div>
      )}
      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Delete Transfer"
        message="Are you sure you want to delete this stock transfer? This action cannot be undone."
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default StockTransferManager;
